import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  Tab,
  TextField,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CookieJs from "js-cookie";
import get from "lodash/get";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import coaService from "../../services/coa";
import groupService from "../../services/group";
import plService from "../../services/pl";
import queryDefault from "../../utils/queryDefault";

type TabValue = "list" | "summary_branch" | "summary_region" | "bep";

const formatNumber = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return parsed.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const toDateParam = (value: Dayjs | null, fallback: string) => {
  if (!value || !value.isValid()) {
    return fallback;
  }

  return value.format("YYYY-MM-DD");
};

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function ListPL() {
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");

  const today = dayjs();
  const startOfMonth = dayjs().startOf("month");

  const [tab, setTab] = useState<TabValue>("list");
  const [filters, setFilters] = useState({
    branch_group_ids: [] as number[],
    coa_codes: [] as string[],
    date_start: startOfMonth.format("YYYY-MM-DD"),
    date_end: today.format("YYYY-MM-DD"),
  });
  const [summaryBranchFilter, setSummaryBranchFilter] = useState("");
  const [summaryRegionFilter, setSummaryRegionFilter] = useState("");
  const [bepBranchGroupFilter, setBepBranchGroupFilter] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadingMutasi, setUploadingMutasi] = useState(false);

  const listParams = useMemo(() => {
    return {
      branch_group_id:
        filters.branch_group_ids.length > 0
          ? filters.branch_group_ids.join(",")
          : undefined,
      coa_code: filters.coa_codes.length > 0 ? filters.coa_codes.join(",") : undefined,
      date_start: filters.date_start,
      date_end: filters.date_end,
    };
  }, [filters]);

  const summaryParams = useMemo(
    () => ({
      date_start: filters.date_start,
      date_end: filters.date_end,
    }),
    [filters.date_start, filters.date_end]
  );

  const branchGroups = useQuery(
    ["branch-group-options"],
    () => groupService.getAllBranchGroup({ keyword: "" }),
    {
      ...queryDefault,
    }
  );

  const coaList = useQuery(
    ["coa-options"],
    () => coaService.listCOA(),
    {
      ...queryDefault,
    }
  );

  const listPL = useQuery(
    ["list-pl", listParams],
    ({ queryKey }) => plService.listPL(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const summaryBranch = useQuery(
    ["summary-pl-branch", summaryParams],
    ({ queryKey }) => plService.getSummaryBranch(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const summaryRegion = useQuery(
    ["summary-pl-region", summaryParams],
    ({ queryKey }) => plService.getSummaryRegion(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const bepData = useQuery(
    ["bep-pl"],
    () => plService.getBEP(),
    {
      ...queryDefault,
    }
  );

  const summaryBranchData = get(summaryBranch, "data.data", []);
  const summaryBranchTotals = summaryBranchData.reduce(
    (acc: { totalIncome: number; totalExpense: number; netProfit: number }, item: any) => {
      return {
        totalIncome: acc.totalIncome + Number(get(item, "total_income", 0)),
        totalExpense: acc.totalExpense + Number(get(item, "total_expense", 0)),
        netProfit: acc.netProfit + Number(get(item, "net_profit", 0)),
      };
    },
    { totalIncome: 0, totalExpense: 0, netProfit: 0 }
  );

  const summaryRegionData = get(summaryRegion, "data.data", []);
  const summaryRegionTotalNetProfit = summaryRegionData.reduce(
    (acc: number, item: any) => acc + Number(get(item, "net_profit", 0)),
    0
  );

  const filteredSummaryBranchData = useMemo(() => {
    const keyword = summaryBranchFilter.trim().toLowerCase();
    if (!keyword) {
      return summaryBranchData;
    }

    return summaryBranchData.filter((item: any) =>
      String(get(item, "branch_group_name", "")).toLowerCase().includes(keyword)
    );
  }, [summaryBranchData, summaryBranchFilter]);

  const filteredSummaryRegionData = useMemo(() => {
    const keyword = summaryRegionFilter.trim().toLowerCase();
    if (!keyword) {
      return summaryRegionData;
    }

    return summaryRegionData.filter((item: any) =>
      String(get(item, "region", "")).toLowerCase().includes(keyword)
    );
  }, [summaryRegionData, summaryRegionFilter]);

  const filteredBEPData = useMemo(() => {
    const keyword = bepBranchGroupFilter.trim().toLowerCase();
    const data = get(bepData, "data.data", []);

    if (!keyword) {
      return data;
    }

    return data.filter((item: any) =>
      String(get(item, "branch_group_name", "")).toLowerCase().includes(keyword)
    );
  }, [bepData, bepBranchGroupFilter]);

  const bepChartsByGroup = useMemo(() => {
    const groupMap = new Map<
      string,
      {
        branch_group_id: number;
        branch_group_name: string;
        regions: Set<string>;
        branch_codes: Set<string>;
        bep: number;
        monthlyMap: Map<
          string,
          {
            year: number;
            month: number;
            total_income: number;
            current_month_expense: number;
            cumulative_expense: number;
            is_bep: boolean;
          }
        >;
      }
    >();

    filteredBEPData.forEach((item: any) => {
      const groupId = Number(get(item, "branch_group_id", 0));
      const groupName = String(get(item, "branch_group_name", "-"));
      const groupKey = `${groupId}-${groupName}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          branch_group_id: groupId,
          branch_group_name: groupName,
          regions: new Set<string>(),
          branch_codes: new Set<string>(),
          bep: Number(get(item, "bep", 0)),
          monthlyMap: new Map(),
        });
      }

      const groupData = groupMap.get(groupKey)!;
      const region = String(get(item, "region", "")).trim();
      const branchCode = String(get(item, "kode_cabang", "")).trim();
      if (region) {
        groupData.regions.add(region);
      }
      if (branchCode) {
        groupData.branch_codes.add(branchCode);
      }

      if (!groupData.bep) {
        groupData.bep = Number(get(item, "bep", 0));
      }

      const monthlyData = get(item, "monthly_data", []);
      monthlyData.forEach((entry: any) => {
        const year = Number(get(entry, "year", 0));
        const month = Number(get(entry, "month", 0));
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;

        if (!groupData.monthlyMap.has(monthKey)) {
          groupData.monthlyMap.set(monthKey, {
            year,
            month,
            total_income: 0,
            current_month_expense: 0,
            cumulative_expense: 0,
            is_bep: false,
          });
        }

        const current = groupData.monthlyMap.get(monthKey)!;
        current.total_income += Number(get(entry, "total_income", 0));
        current.current_month_expense += Number(get(entry, "current_month_expense", 0));
        current.cumulative_expense += Number(get(entry, "cumulative_expense", 0));
        current.is_bep = current.is_bep || Boolean(get(entry, "is_bep", false));
      });
    });

    return Array.from(groupMap.values())
      .map((groupData) => {
        const monthlyData = Array.from(groupData.monthlyMap.values())
          .sort((a, b) => {
            if (a.year !== b.year) {
              return a.year - b.year;
            }
            return a.month - b.month;
          })
          .map((entry) => ({
            month_label: `${String(entry.month).padStart(2, "0")}/${entry.year}`,
            total_income: entry.total_income,
            current_month_expense: entry.current_month_expense,
            cumulative_expense: entry.cumulative_expense,
            bep_marker: entry.is_bep ? entry.cumulative_expense : null,
            is_bep: entry.is_bep,
          }));

        return {
          branch_group_id: groupData.branch_group_id,
          branch_group_name: groupData.branch_group_name,
          regions: Array.from(groupData.regions).join(", ") || "-",
          branch_codes: Array.from(groupData.branch_codes).join(", ") || "-",
          bep: groupData.bep,
          monthlyData,
        };
      })
      .sort((a, b) => a.branch_group_name.localeCompare(b.branch_group_name));
  }, [filteredBEPData]);

  const downloadBulkTemplate = useMutation("download-pl-bulk-template", plService.downloadBulkTemplate, {
    onError: () => {
      notification.onOpen({
        message: "Something went wrong when downloading template",
        type: "error",
        position: "top",
      });
    },
  });

  const downloadSummaryBranch = useMutation(
    "download-summary-pl-branch",
    plService.downloadSummaryBranch,
    {
      onError: () => {
        notification.onOpen({
          message: "Something went wrong when downloading summary branch",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadSummaryRegion = useMutation(
    "download-summary-pl-region",
    plService.downloadSummaryRegion,
    {
      onError: () => {
        notification.onOpen({
          message: "Something went wrong when downloading summary region",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadMutasiTemplate = useMutation(
    "download-pl-mutasi-template",
    plService.downloadMutasiTemplate,
    {
      onError: () => {
        notification.onOpen({
          message: "Something went wrong when downloading mutasi template",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadBEP = useMutation("download-bep", plService.downloadBEP, {
    onError: () => {
      notification.onOpen({
        message: "Something went wrong when downloading BEP",
        type: "error",
        position: "top",
      });
    },
  });

  const uploadBulk = useMutation("upload-pl-bulk", plService.uploadBulkTemplate, {
    onSuccess: (response) => {
      setUploading(false);
      notification.onOpen({
        message: response?.message || "File was successfully uploaded!",
        type: "success",
        position: "top",
      });
      listPL.refetch();
      summaryBranch.refetch();
      summaryRegion.refetch();
    },
    onError: () => {
      setUploading(false);
      notification.onOpen({
        message: "Something wrong happened when uploading bulk template",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const uploadMutasi = useMutation("upload-pl-mutasi", plService.uploadMutasiTemplate, {
    onSuccess: (response) => {
      setUploadingMutasi(false);
      notification.onOpen({
        message: response?.message || "File was successfully uploaded!",
        type: "success",
        position: "top",
      });
      listPL.refetch();
      summaryBranch.refetch();
      summaryRegion.refetch();
    },
    onError: () => {
      setUploadingMutasi(false);
      notification.onOpen({
        message: "Something wrong happened when uploading mutasi template",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xls" && extension !== "xlsx") {
      notification.onOpen({
        message: "Please upload excel file only (.xls or .xlsx)",
        type: "error",
        position: "top",
      });
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("admin_name", user?.username || user?.name || "");

    setUploading(true);
    uploadBulk.mutate(formData);
    event.target.value = "";
  };

  const handleUploadMutasiFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xls" && extension !== "xlsx") {
      notification.onOpen({
        message: "Please upload excel file only (.xls or .xlsx)",
        type: "error",
        position: "top",
      });
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingMutasi(true);
    uploadMutasi.mutate(formData);
    event.target.value = "";
  };

  const listColumns = [
    {
      field: "branch_group_name",
      headerName: "Branch Group",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "kode_cabang",
      headerName: "Kode Cabang",
      minWidth: 140,
      flex: 1,
      filterable: false,
    },
    {
      field: "transaction_date",
      headerName: "Transaction Date",
      minWidth: 140,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_code",
      headerName: "COA Code",
      minWidth: 120,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_description",
      headerName: "COA Description",
      minWidth: 220,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_type",
      headerName: "COA Type",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 140,
      flex: 1,
      filterable: false,
      renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 220,
      flex: 1,
      filterable: false,
    },
    {
      field: "accurate_no",
      headerName: "Accurate No",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 100,
      flex: 1,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to={`/app/pl/edit/${get(params, "row.id")}`}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <Breadcrumb label="P&L" breadcrumbs={[{ label: "P&L", href: "/app/pl" }]} />

      <ProtectedPermissionPage acceptPermissions={[1, 11]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Date Start</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(filters.date_start)}
                    onChange={(newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        date_start: toDateParam(newValue, prev.date_start),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Date End</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(filters.date_end)}
                    onChange={(newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        date_end: toDateParam(newValue, prev.date_end),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Branch Group</FormLabel>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={get(branchGroups, "data.data", [])}
                  value={get(branchGroups, "data.data", []).filter((item: any) =>
                    filters.branch_group_ids.includes(Number(item.id))
                  )}
                  onChange={(_, selectedOptions: any[]) => {
                    setFilters((prev) => ({
                      ...prev,
                      branch_group_ids: selectedOptions.map((item: any) => Number(item.id)),
                    }));
                  }}
                  getOptionLabel={(option: any) => option.name || ""}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option: any) =>
                      String(option.name || "").toLowerCase().includes(inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="All Branch Group" />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>COA</FormLabel>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={get(coaList, "data.data", [])}
                  value={get(coaList, "data.data", []).filter((item: any) =>
                    filters.coa_codes.includes(String(item.code))
                  )}
                  onChange={(_, selectedOptions: any[]) => {
                    setFilters((prev) => ({
                      ...prev,
                      coa_codes: selectedOptions.map((item: any) => String(item.code)),
                    }));
                  }}
                  getOptionLabel={(option: any) => `${option.code} - ${option.description}`}
                  isOptionEqualToValue={(option: any, value: any) => option.code === value.code}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option: any) =>
                      `${option.code} ${option.description}`
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="All COA" />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={() => downloadBulkTemplate.mutate()}
              loading={downloadBulkTemplate.isLoading}
            >
              Download Bulk Template
            </LoadingButton>

            <LoadingButton variant="outlined" component="label" loading={uploading}>
              Upload Bulk Template
              <input
                onChange={handleUploadFile}
                type="file"
                hidden
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </LoadingButton>

            <LoadingButton
              variant="contained"
              onClick={() => downloadMutasiTemplate.mutate()}
              loading={downloadMutasiTemplate.isLoading}
            >
              Download Mutasi Template
            </LoadingButton>

            <LoadingButton variant="outlined" component="label" loading={uploadingMutasi}>
              Upload Mutasi Template
              <input
                onChange={handleUploadMutasiFile}
                type="file"
                hidden
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </LoadingButton>

            <Button variant="contained" component={RouterLink} to="/app/pl/create">
              Add New Record
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Tabs value={tab} onChange={(_, value: TabValue) => setTab(value)}>
            <Tab value="list" label="List P&L" />
            <Tab value="summary_branch" label="Summary Branch" />
            <Tab value="summary_region" label="Summary Region" />
            <Tab value="bep" label="BEP" />
          </Tabs>
        </Box>

        {tab === "list" && (
          <Box sx={{ mt: 2 }}>
            <DataGrid
              autoHeight
              loading={listPL.isLoading}
              getRowId={(record) => get(record, "id")}
              rows={get(listPL, "data.data", [])}
              rowCount={get(listPL, "data.data", []).length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={listColumns as any}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        )}

        {tab === "summary_branch" && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <LoadingButton
                  variant="contained"
                  onClick={() => downloadSummaryBranch.mutate(summaryParams)}
                  loading={downloadSummaryBranch.isLoading}
                >
                  Download Summary Branch
                </LoadingButton>
                <TextField
                  size="small"
                  placeholder="Filter branch group"
                  value={summaryBranchFilter}
                  onChange={(event) => setSummaryBranchFilter(event.target.value)}
                />
              </Stack>
            </Box>

            <DataGrid
              autoHeight
              loading={summaryBranch.isLoading}
              getRowId={(record) => `${get(record, "branch_group_id")}-${get(record, "kode_cabang")}`}
              rows={filteredSummaryBranchData}
              rowCount={filteredSummaryBranchData.length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={[
                {
                  field: "branch_group_name",
                  headerName: "Branch Group",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "kode_cabang",
                  headerName: "Kode Cabang",
                  minWidth: 140,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "region",
                  headerName: "Region",
                  minWidth: 120,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "total_income",
                  headerName: "Total Income",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "total_expense",
                  headerName: "Total Expense",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "net_profit",
                  headerName: "Net Profit",
                  minWidth: 240,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
              ]}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />

            <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography fontWeight={700}>TOTAL</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }}>
                <Typography>
                  Total Income: <strong>{formatNumber(summaryBranchTotals.totalIncome)}</strong>
                </Typography>
                <Typography>
                  Total Expense: <strong>{formatNumber(summaryBranchTotals.totalExpense)}</strong>
                </Typography>
                <Typography>
                  Total Net Profit: <strong>{formatNumber(summaryBranchTotals.netProfit)}</strong>
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}

        {tab === "summary_region" && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <LoadingButton
                  variant="contained"
                  onClick={() => downloadSummaryRegion.mutate(summaryParams)}
                  loading={downloadSummaryRegion.isLoading}
                >
                  Download Summary Region
                </LoadingButton>
                <TextField
                  size="small"
                  placeholder="Filter region"
                  value={summaryRegionFilter}
                  onChange={(event) => setSummaryRegionFilter(event.target.value)}
                />
              </Stack>
            </Box>

            <DataGrid
              autoHeight
              loading={summaryRegion.isLoading}
              getRowId={(record) => `${get(record, "region")}-${get(record, "net_profit")}`}
              rows={filteredSummaryRegionData}
              rowCount={filteredSummaryRegionData.length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={[
                {
                  field: "region",
                  headerName: "Region",
                  minWidth: 220,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "total_income",
                  headerName: "Total Income",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "net_profit",
                  headerName: "Net Profit",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "profit_ratio",
                  headerName: "Profit Ratio (%)",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "profit_contribution",
                  headerName: "Profit Contribution (%)",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
              ]}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />

            <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography fontWeight={700}>TOTAL</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }}>
                <Typography>
                  Total Net Profit: <strong>{formatNumber(summaryRegionTotalNetProfit)}</strong>
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}

        {tab === "bep" && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <LoadingButton
                  variant="contained"
                  onClick={() => downloadBEP.mutate()}
                  loading={downloadBEP.isLoading}
                >
                  Download BEP
                </LoadingButton>
                <TextField
                  size="small"
                  placeholder="Filter branch group"
                  value={bepBranchGroupFilter}
                  onChange={(event) => setBepBranchGroupFilter(event.target.value)}
                />
              </Stack>
            </Box>

            {bepData.isLoading && <Typography>Loading BEP data...</Typography>}

            {!bepData.isLoading && bepChartsByGroup.length === 0 && (
              <Typography>Sorry, data is not found</Typography>
            )}

            <Stack spacing={3}>
              {bepChartsByGroup.map((item: any) => {
                const monthlyData = item.monthlyData;

                return (
                  <Box
                    key={`${get(item, "branch_group_id", "")}`}
                    sx={{
                      p: 2,
                      border: "1px solid #d7deea",
                      borderRadius: 2,
                      background: "linear-gradient(180deg, #ffffff 0%, #f7faff 100%)",
                    }}
                  >
                    <Typography fontWeight={700}>{get(item, "branch_group_name", "-")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Kode Cabang: {get(item, "branch_codes", "-")} | Region: {get(item, "regions", "-")} | BEP: {formatNumber(get(item, "bep", 0))}
                    </Typography>

                    <Box sx={{ width: "100%", height: 360 }}>
                      <ResponsiveContainer>
                        <ComposedChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#dce5f2" />
                          <XAxis dataKey="month_label" />
                          <YAxis
                            tickFormatter={(value: any) =>
                              new Intl.NumberFormat("id-ID", {
                                notation: "compact",
                                maximumFractionDigits: 1,
                              }).format(Number(value || 0))
                            }
                          />
                          <Tooltip
                            formatter={(value: any, name: string, props: any) => {
                              if (name === "is_bep") {
                                return [props?.payload?.is_bep ? "true" : "false", "is_bep"];
                              }
                              return [formatNumber(value), name];
                            }}
                            labelFormatter={(label: any) => `Month: ${label}`}
                          />
                          <Legend />
                          <Bar dataKey="total_income" name="Total Income" fill="#2E7D32" radius={[6, 6, 0, 0]} />
                          <Bar
                            dataKey="current_month_expense"
                            name="Current Month Expense"
                            fill="#C62828"
                            radius={[6, 6, 0, 0]}
                          />
                          <Line
                            type="monotone"
                            dataKey="cumulative_expense"
                            name="Cumulative Expense"
                            stroke="#1565C0"
                            strokeWidth={3}
                            dot={{ r: 3, stroke: "#1565C0", strokeWidth: 2, fill: "#ffffff" }}
                          />
                          <ReferenceLine
                            y={Number(get(item, "bep", 0))}
                            label="BEP"
                            stroke="#6A1B9A"
                            strokeDasharray="6 4"
                          />
                          <Scatter dataKey="bep_marker" name="BEP Reached" fill="#FF8F00" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Orange marker indicates month with <strong>BEP</strong>.
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
